import { useState, useEffect } from "react";
import { 
  Activity, 
  AlertTriangle, 
  AlertCircle,
  CheckCircle,
  Info,
  CreditCard,
  Key,
  Mail,
  RefreshCw,
  Search,
  Filter,
  ChevronDown,
  ChevronRight,
  Clock,
  User,
  ExternalLink
} from "lucide-react";
import axios from "axios";
import { API } from "../../App";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { toast } from "sonner";

const AdminActivityLog = () => {
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    category: "",
    severity: "",
    search: ""
  });
  const [pagination, setPagination] = useState({
    limit: 50,
    offset: 0,
    total: 0,
    hasMore: false
  });
  const [expandedLog, setExpandedLog] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    fetchStats();
  }, [refreshKey]);

  useEffect(() => {
    const loadLogs = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem("admin_token");
        const params = new URLSearchParams();
        
        if (filters.category) params.append("category", filters.category);
        if (filters.severity) params.append("severity", filters.severity);
        if (filters.search) params.append("search", filters.search);
        params.append("limit", pagination.limit);
        params.append("offset", pagination.offset);
        
        const response = await axios.get(`${API}/admin/activity-logs?${params}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        setLogs(response.data.logs);
        setPagination(prev => ({
          ...prev,
          total: response.data.total,
          hasMore: response.data.has_more
        }));
      } catch (error) {
        toast.error("Failed to fetch activity logs");
      } finally {
        setLoading(false);
      }
    };
    
    loadLogs();
  }, [filters, pagination.offset, pagination.limit, refreshKey]);

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem("admin_token");
      const response = await axios.get(`${API}/admin/activity-logs/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error) {
      console.error("Failed to fetch stats:", error);
    }
  };

  const handleRefresh = () => {
    setRefreshKey(prev => prev + 1);
    toast.success("Logs refreshed");
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPagination(prev => ({ ...prev, offset: 0 }));
  };

  const handleClearFilters = () => {
    setFilters({ category: "", severity: "", search: "" });
    setPagination(prev => ({ ...prev, offset: 0 }));
  };

  const getSeverityIcon = (severity) => {
    switch (severity) {
      case "critical":
        return <AlertCircle className="w-4 h-4 text-red-500" />;
      case "error":
        return <AlertTriangle className="w-4 h-4 text-orange-500" />;
      case "warning":
        return <AlertTriangle className="w-4 h-4 text-yellow-500" />;
      default:
        return <Info className="w-4 h-4 text-blue-500" />;
    }
  };

  const getSeverityBadge = (severity) => {
    const styles = {
      critical: "bg-red-500/20 text-red-400 border-red-500/30",
      error: "bg-orange-500/20 text-orange-400 border-orange-500/30",
      warning: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30",
      info: "bg-blue-500/20 text-blue-400 border-blue-500/30"
    };
    return styles[severity] || styles.info;
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case "payment":
        return <CreditCard className="w-4 h-4" />;
      case "auth":
        return <Key className="w-4 h-4" />;
      case "email":
        return <Mail className="w-4 h-4" />;
      case "subscription":
        return <RefreshCw className="w-4 h-4" />;
      default:
        return <Activity className="w-4 h-4" />;
    }
  };

  const getCategoryBadge = (category) => {
    const styles = {
      payment: "bg-emerald-500/20 text-emerald-400",
      auth: "bg-purple-500/20 text-purple-400",
      email: "bg-cyan-500/20 text-cyan-400",
      subscription: "bg-amber-500/20 text-amber-400",
      system: "bg-slate-500/20 text-slate-400"
    };
    return styles[category] || styles.system;
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  const formatRelativeTime = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return formatDate(dateStr);
  };

  return (
    <div className="space-y-6" data-testid="admin-activity-log">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-serif" data-testid="activity-log-title">Activity Log</h1>
          <p className="text-sm text-muted-foreground mt-1">
            System events, failures, and critical notifications
          </p>
        </div>
        <Button 
          onClick={handleRefresh} 
          variant="outline" 
          className="gap-2"
          data-testid="refresh-logs-btn"
        >
          <RefreshCw className="w-4 h-4" />
          Refresh
        </Button>
      </div>

      {/* Stats Overview */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="activity-stats">
          <div className="glass-card p-4">
            <div className="flex items-center gap-2 text-muted-foreground text-sm">
              <Activity className="w-4 h-4" />
              Total Events
            </div>
            <p className="text-2xl font-medium mt-1">{stats.total_logs.toLocaleString()}</p>
          </div>
          
          <div className="glass-card p-4">
            <div className="flex items-center gap-2 text-muted-foreground text-sm">
              <AlertTriangle className="w-4 h-4 text-orange-500" />
              Errors (24h)
            </div>
            <p className="text-2xl font-medium mt-1 text-orange-400">{stats.errors_last_24h}</p>
          </div>
          
          <div className="glass-card p-4">
            <div className="flex items-center gap-2 text-muted-foreground text-sm">
              <AlertCircle className="w-4 h-4 text-red-500" />
              Critical (24h)
            </div>
            <p className="text-2xl font-medium mt-1 text-red-400">{stats.critical_last_24h}</p>
          </div>
          
          <div className="glass-card p-4">
            <div className="flex items-center gap-2 text-muted-foreground text-sm">
              <CreditCard className="w-4 h-4 text-emerald-500" />
              Payment Events
            </div>
            <p className="text-2xl font-medium mt-1">{stats.by_category?.payment || 0}</p>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="glass-card p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          {/* Search */}
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search by title, message, email..."
              value={filters.search}
              onChange={(e) => handleFilterChange("search", e.target.value)}
              className="pl-10"
              data-testid="search-logs-input"
            />
          </div>
          
          {/* Category Filter */}
          <select
            value={filters.category}
            onChange={(e) => handleFilterChange("category", e.target.value)}
            className="px-3 py-2 bg-white/5 border border-white/10 rounded-md text-sm"
            data-testid="category-filter"
          >
            <option value="">All Categories</option>
            <option value="payment">Payment</option>
            <option value="auth">Authentication</option>
            <option value="email">Email</option>
            <option value="subscription">Subscription</option>
            <option value="system">System</option>
          </select>
          
          {/* Severity Filter */}
          <select
            value={filters.severity}
            onChange={(e) => handleFilterChange("severity", e.target.value)}
            className="px-3 py-2 bg-white/5 border border-white/10 rounded-md text-sm"
            data-testid="severity-filter"
          >
            <option value="">All Severities</option>
            <option value="critical">Critical</option>
            <option value="error">Error</option>
            <option value="warning">Warning</option>
            <option value="info">Info</option>
          </select>
          
          {(filters.category || filters.severity || filters.search) && (
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={handleClearFilters}
              data-testid="clear-filters-btn"
            >
              Clear
            </Button>
          )}
        </div>
      </div>

      {/* Logs List */}
      <div className="glass-card overflow-hidden" data-testid="activity-logs-list">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="spinner" />
          </div>
        ) : logs.length === 0 ? (
          <div className="text-center py-12">
            <Activity className="w-12 h-12 mx-auto text-muted-foreground mb-3" />
            <p className="text-muted-foreground">No activity logs found</p>
            {(filters.category || filters.severity || filters.search) && (
              <p className="text-sm text-muted-foreground mt-1">
                Try adjusting your filters
              </p>
            )}
          </div>
        ) : (
          <div className="divide-y divide-white/5">
            {logs.map((log) => (
              <div 
                key={log.log_id} 
                className="hover:bg-white/5 transition-colors"
                data-testid={`log-entry-${log.log_id}`}
              >
                {/* Main Row */}
                <div 
                  className="p-4 cursor-pointer"
                  onClick={() => setExpandedLog(expandedLog === log.log_id ? null : log.log_id)}
                >
                  <div className="flex items-start gap-3">
                    {/* Expand Icon */}
                    <div className="mt-1">
                      {expandedLog === log.log_id ? (
                        <ChevronDown className="w-4 h-4 text-muted-foreground" />
                      ) : (
                        <ChevronRight className="w-4 h-4 text-muted-foreground" />
                      )}
                    </div>
                    
                    {/* Severity Icon */}
                    <div className="mt-0.5">
                      {getSeverityIcon(log.severity)}
                    </div>
                    
                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium">{log.title}</span>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${getCategoryBadge(log.category)}`}>
                          {log.category}
                        </span>
                        <span className={`text-xs px-2 py-0.5 rounded-full border ${getSeverityBadge(log.severity)}`}>
                          {log.severity}
                        </span>
                      </div>
                      
                      <p className="text-sm text-muted-foreground mt-1 line-clamp-1">
                        {log.message}
                      </p>
                      
                      <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {formatRelativeTime(log.created_at)}
                        </span>
                        {log.user_email && (
                          <span className="flex items-center gap-1">
                            <User className="w-3 h-3" />
                            {log.user_email}
                          </span>
                        )}
                        <span className="text-muted-foreground/50 font-mono text-xs">
                          {log.event_type}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Expanded Details */}
                {expandedLog === log.log_id && (
                  <div className="px-4 pb-4 ml-11 border-t border-white/5 pt-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                      <div>
                        <p className="text-muted-foreground text-xs uppercase mb-1">Full Message</p>
                        <p className="text-white/90">{log.message}</p>
                      </div>
                      
                      <div>
                        <p className="text-muted-foreground text-xs uppercase mb-1">Timestamp</p>
                        <p className="text-white/90">{formatDate(log.created_at)}</p>
                      </div>
                      
                      {log.external_id && (
                        <div>
                          <p className="text-muted-foreground text-xs uppercase mb-1">External ID</p>
                          <p className="text-white/90 font-mono text-xs">{log.external_id}</p>
                        </div>
                      )}
                      
                      {log.photographer_id && (
                        <div>
                          <p className="text-muted-foreground text-xs uppercase mb-1">Photographer ID</p>
                          <a 
                            href={`/admin/photographers?search=${log.photographer_id}`}
                            className="text-primary hover:underline font-mono text-xs flex items-center gap-1"
                          >
                            {log.photographer_id}
                            <ExternalLink className="w-3 h-3" />
                          </a>
                        </div>
                      )}
                      
                      {log.metadata && Object.keys(log.metadata).length > 0 && (
                        <div className="md:col-span-2">
                          <p className="text-muted-foreground text-xs uppercase mb-1">Additional Data</p>
                          <pre className="bg-black/30 rounded p-2 text-xs overflow-x-auto">
                            {JSON.stringify(log.metadata, null, 2)}
                          </pre>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
        
        {/* Pagination */}
        {!loading && logs.length > 0 && (
          <div className="flex items-center justify-between p-4 border-t border-white/5">
            <p className="text-sm text-muted-foreground">
              Showing {pagination.offset + 1}-{Math.min(pagination.offset + logs.length, pagination.total)} of {pagination.total}
            </p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={pagination.offset === 0}
                onClick={() => setPagination(prev => ({ ...prev, offset: Math.max(0, prev.offset - prev.limit) }))}
                data-testid="prev-page-btn"
              >
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={!pagination.hasMore}
                onClick={() => setPagination(prev => ({ ...prev, offset: prev.offset + prev.limit }))}
                data-testid="next-page-btn"
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminActivityLog;
